<template>
  <ak-card-header class="flex flex--center">
    <div class="flex__item flex flex--center">
      <h4 class="color-grey">
        {{ opportunityType }} {{ opportunitiesBranding || 'Opportunities' }}
      </h4>
      <i
        v-if="opportunityType === 'All'"
        class="ml-tiny fa fa-solid fa-user-group"
      />
      <i v-if="opportunityType === 'My'" class="ml-tiny fa fa-solid fa-user" />
      <ak-dropdown size="auto" aria-role="list" class="ml-tiny leads-dropdown">
        <template #trigger>
          <ak-button color="transparent" size="small" circle>
            <span class="caret"></span>
          </ak-button>
        </template>
        <template #default>
          <div>
            <ak-dropdown-item
              class="flex flex--center pt-0 pb-tiny px-tiny"
              @click="changeOpportunityType('All')"
            >
              <i class="fa fa-solid fa-user-group mr-tiny min-width-20" />
              <span class="text-nowrap">All {{ opportunitiesBranding }}</span>
            </ak-dropdown-item>
            <ak-dropdown-item
              class="flex flex--center py-0 px-tiny"
              @click="changeOpportunityType('My')"
            >
              <i class="fa fa-solid fa-user mr-tiny min-width-20" />
              <span class="text-nowrap">My {{ opportunitiesBranding }}</span>
            </ak-dropdown-item>
          </div>
        </template>
      </ak-dropdown>
    </div>
    <div>
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <ak-button
            size="small"
            circle
            color="transparent"
            @click="opportunityModal({ add: add })"
          >
            <i class="color-primary icon-plus" />
          </ak-button>
        </template>
        Add New
      </ak-tooltip>
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <ak-button
            size="small"
            circle
            color="transparent"
            @click="changeWidget()"
          >
            <i class="color-grey-light icon-cog" />
          </ak-button>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column overflow-y h-full">
    <ak-display-table v-if="isLoading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell>
          <div class="c-faux-text width-125" />
        </ak-display-table-cell>
        <ak-display-table-cell>
          <div class="c-faux-text width-50" />
        </ak-display-table-cell>
        <ak-display-table-cell class="cell--1">
          <div class="c-faux-text width-50" />
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>

    <ak-table
      v-if="size !== 'large'"
      v-model:data="opportunities"
      hoverable
      hide-head
      background
      spacing="medium"
      :empty-state="emptyState"
      :class="{ 'h-full flex': !opportunities.length }"
    >
      <template #default="{ row }">
        <ak-table-column>
          <router-link
            :to="{ name: 'opportunitiesEdit', params: { id: row._id } }"
            @click="row.clicked = true"
          >
            {{ row.name }}
          </router-link>
        </ak-table-column>
        <ak-table-column class="text-nowrap">
          <router-link
            :to="{ name: 'opportunitiesEdit', params: { id: row._id } }"
            @click="row.clicked = true"
          >
            <strong class="color-primary"
              >{{ getCurrencyCode(row.currency) }}{{ row.amount }}</strong
            >
          </router-link>
        </ak-table-column>
        <ak-table-column class="text-nowrap cell--1">
          <router-link
            :to="{ name: 'opportunitiesEdit', params: { id: row._id } }"
            @click="row.clicked = true"
          >
            {{ row.status }}
          </router-link>
        </ak-table-column>
      </template>
    </ak-table>

    <ak-table
      v-if="size === 'large'"
      v-model:data="opportunities"
      hoverable
      background
      spacing="medium"
      :class="{ 'h-full flex': !opportunities.length }"
      :empty-state="emptyState"
    >
      <template #default="{ row }">
        <ak-table-column label="Name">
          <router-link
            :to="{ name: 'opportunitiesEdit', params: { id: row._id } }"
            @click="row.clicked = true"
          >
            {{ row.name }}
          </router-link>
        </ak-table-column>
        <ak-table-column label="Amount">
          <router-link
            :to="{ name: 'opportunitiesEdit', params: { id: row._id } }"
            @click="row.clicked = true"
          >
            <strong class="color-primary"
              >{{ getCurrencyCode(row.currency) }}{{ row.amount }}</strong
            >
          </router-link>
        </ak-table-column>
        <ak-table-column label="Stage">
          <router-link
            :to="{ name: 'opportunitiesEdit', params: { id: row._id } }"
            @click="row.clicked = true"
          >
            {{ row.status }}
          </router-link>
        </ak-table-column>
        <ak-table-column label="Owner">
          <router-link
            :to="{ name: 'opportunitiesEdit', params: { id: row._id } }"
            @click="row.clicked = true"
          >
            <ak-pack>
              <ak-pack-item>
                <ak-avatar
                  :string="row.owner.name ? row.owner.name : '-'"
                  :size="30"
                />
              </ak-pack-item>
              <ak-pack-item>
                {{ row.owner.name || '-' }}
              </ak-pack-item>
            </ak-pack>
          </router-link>
        </ak-table-column>
      </template>
      <template #empty>
        <div></div>
      </template>
    </ak-table>
  </ak-card-body>
</template>

<script>
import { accountObj, agencyObj, userObj } from '@akerolabs/akero-vue/src/store'
import { defineComponent, ref, computed, onMounted } from 'vue'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import { currencyCodeToSymbol } from '@akerolabs/akero-vue/src/filters/currency'

export default defineComponent({
  name: 'AkWidgetOpportunities',

  components: {
    [CardHeader.name]: CardHeader,
  },

  props: {
    /**
     * @type {Function}
     * @description change widget function
     */
    changeWidget: {
      default: () => ({}),
      type: Function,
    },
    /**
     * @type {Function}
     * @description Open add opportunity modal
     */
    opportunityModal: {
      default: () => ({}),
      type: Function,
    },
    /**
     * @type {String}
     * @description check if large or small widget
     */
    size: {
      default: '',
      type: String,
    },
    /**
     * @type {Function}
     * @description change tyle of opportunities list
     */
    changeType: {
      default: () => ({}),
      type: Function,
    },
  },

  setup(props) {
    let n = 10
    const opportunities = ref([])
    const isLoading = ref(true)
    let opportunityType = ref('All')

    /**
     * @description User defined lead branding
     */
    const opportunitiesBranding = computed(() => {
      return entityBranding('opportunities.plural', 'Opportunities')
    })

    /**
     * @description Makes an api request to request leads for account/agency
     */
    const fetchOpportunities = async () => {
      isLoading.value = true
      let opportunitiesData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get(
          '/opportunities',
          opportunitiesRequest.value,
        )
        opportunitiesData = data.data
      } catch (ex) {
        opportunitiesData = []
        displayError(ex)
      }
      opportunities.value = opportunitiesData
      isLoading.value = false
    }

    /**
     * @description params for fetchLeads()
     */
    const opportunitiesRequest = computed(() => {
      let owner = undefined
      if (opportunityType.value === 'My') {
        owner = userObj()._id
      }
      let params = {
        account: accountObj()._id,
        agency: agencyObj()._id,
        deleted: JSON.stringify({ $ne: true }),
        per_page: 50,
        status: JSON.stringify({ $nin: ['lost', 'won'] }),
        owner: owner,
      }
      return {
        params,
      }
    })

    // Get currency symbol from string code
    const getCurrencyCode = (str) => {
      return currencyCodeToSymbol(str)
    }

    // Change the type and refresh table
    const changeOpportunityType = async (type) => {
      opportunityType.value = type
      props.changeType()
      await fetchOpportunities()
    }

    /**
     *
     * @description Create empty state for table
     */
    const emptyState = {
      title: entityBranding('opportunities.plural', 'Opportunities'),
      icon: 'briefcase',
      text:
        'There are currently no ' +
        entityBranding('opportunities.plural', 'Opportunities'),
      button: 'Add ' + entityBranding('opportunities.singular', 'Opportunity'),
      click: () => props.opportunityModal({ add: props.add }),
    }

    onMounted(async () => {
      await fetchOpportunities()
    })

    return {
      opportunities,
      opportunitiesBranding,
      isLoading,
      n,
      getCurrencyCode,
      changeOpportunityType,
      opportunityType,
      emptyState,
    }
  },
})
</script>
